import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, FileText, Receipt, Scale, Building2,
  ClipboardList, CreditCard, FolderOpen, Calendar, BarChart3, Menu, X,
  LogOut, FileSearch, Landmark, BookOpen, UserCog, Archive, Settings,
  Briefcase, ChevronDown, Shield, UserCircle, CalendarDays, ListChecks,
  KeyRound, Lock, Wrench,
} from 'lucide-react';
import { hasRole } from '../utils/helpers';
import NotificationBell from './NotificationBell';
import WellnessReminders from './WellnessReminders';
import { ThemeSwitcherButton } from './ThemeSwitcher';
import { useTheme } from '../themes/ThemeContext';

function NavItem({ to, icon: Icon, label, exact, onClick }) {
  return (
    <NavLink
      to={to}
      end={exact}
      title={label}
      onClick={onClick}
      className="nav-item flex items-center gap-3 py-2.5 text-sm w-full"
      style={({ isActive }) =>
        isActive
          ? {
              borderLeft: '3px solid var(--sidebar-nav-active-border)',
              background: 'var(--sidebar-nav-active-bg)',
              color: 'var(--sidebar-accent)',
              paddingLeft: '17px',
              paddingRight: '20px',
              fontWeight: 600,
            }
          : {
              borderLeft: '3px solid transparent',
              color: 'var(--sidebar-text)',
              paddingLeft: '17px',
              paddingRight: '20px',
            }
      }
    >
      <Icon size={17} className="flex-shrink-0" />
      <span className="nav-label whitespace-nowrap">{label}</span>
    </NavLink>
  );
}

// Cupertino macOS-style nav item
function CNavItem({ to, icon: Icon, label, exact, onClick }) {
  return (
    <NavLink
      to={to}
      end={exact}
      title={label}
      onClick={onClick}
      className="cupertino-nav-item flex items-center gap-2.5 w-full"
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
        fontSize: '13px', fontWeight: isActive ? 600 : 400,
        color: '#1D1D1F', opacity: isActive ? 1 : 0.75,
        background: isActive ? '#EDEDF0' : 'transparent',
        padding: '7px 12px', borderRadius: '8px',
        textDecoration: 'none',
      })}
    >
      <Icon size={15} style={{ color: '#86868B', flexShrink: 0 }} />
      <span className="nav-label whitespace-nowrap">{label}</span>
    </NavLink>
  );
}

// Cupertino section label
function CSectionLabel({ label }) {
  return (
    <div className="rail-hide" style={{
      fontSize: '10px', fontWeight: 600, color: '#86868B',
      letterSpacing: '0.08em', textTransform: 'uppercase',
      padding: '14px 12px 4px',
    }}>
      {label}
    </div>
  );
}

// User avatar dropdown
function UserDropdown({ user, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doLogout = () => { setOpen(false); logout(); navigate('/login'); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center font-bold text-sm hover:ring-2 transition-all flex-shrink-0"
        style={{
          background: 'var(--sidebar-logo-bg)',
          color: 'var(--sidebar-logo-color)',
          ringColor: 'var(--sidebar-accent)',
        }}
      >
        {user?.profile_photo
          ? <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
          : user?.name?.[0]}
      </button>
      {open && (
        <div
          className="absolute right-0 top-11 z-[200] rounded-xl shadow-2xl border min-w-[180px] py-1"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
        >
          <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--card-border)' }}>
            <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{user?.name}</div>
            <div className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{user?.designation || user?.role?.replace(/_/g, ' ')}</div>
          </div>
          <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-gray-50 transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <UserCircle size={15} /> My Profile
          </Link>
          <Link to="/change-password" onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-gray-50 transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <KeyRound size={15} /> Change Password
          </Link>
          <button onClick={doLogout} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// Hoisted out of Layout (#31): defining this inside Layout created a new
// component type per render, remounting the sidebar (and resetting its
// scroll position) every time any Layout state changed.
function Sidebar({ sidebarOpen, isCupertino, close, accountsOpen, setAccountsOpen, NavComponent, can, isAdmin, isSuperAdmin, user, logout, collapsed, onRailEnter, onRailLeave }) {
  return (
    <div
      className={`sidebar-rail ${collapsed ? 'rail-collapsed' : ''} fixed inset-y-0 left-0 z-50 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 ${isCupertino ? '' : 'shadow-2xl'}`}
      onMouseEnter={onRailEnter}
      onMouseLeave={onRailLeave}
      style={{
        background: 'var(--sidebar-bg)',
        width: collapsed ? '64px' : (isCupertino ? '230px' : '240px'),
        borderRight: isCupertino ? '1px solid var(--hairline)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      {isCupertino ? (
        <div className="px-5 py-5" style={{ borderBottom: '1px solid var(--hairline)' }}>
          <div className="flex items-center">
            <span style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.02em' }}>
              KU<span style={{ color: '#B8860B' }}>.</span>
            </span>
            <button className="ml-auto md:hidden" style={{ color: '#1D1D1F' }} onClick={close}><X size={18} /></button>
          </div>
          <div className="rail-hide" style={{ fontSize: '11px', color: '#86868B', marginTop: '2px' }}>Krishna Uday &amp; Associates</div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-5 py-5" style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-lg"
            style={{ background: 'var(--sidebar-logo-bg)', color: 'var(--sidebar-logo-color)' }}
          >
            KU
          </div>
          <div className="rail-hide">
            <div className="font-bold text-sm leading-tight whitespace-nowrap" style={{ color: 'var(--sidebar-text)' }}>Krishna Uday</div>
            <div className="text-xs whitespace-nowrap" style={{ color: 'var(--sidebar-accent)' }}>&amp; Associates</div>
          </div>
          <button className="ml-auto md:hidden" style={{ color: 'var(--sidebar-text)' }} onClick={close}><X size={18} /></button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2" style={isCupertino ? { padding: '8px 10px' } : undefined}>
        {isCupertino && <CSectionLabel label="Overview" />}
        <NavComponent to="/" icon={LayoutDashboard} label="Dashboard" exact onClick={close} />
        {can('clients', 'view') && <NavComponent to="/clients" icon={Users} label="Clients" onClick={close} />}

        {isCupertino && <CSectionLabel label="Compliance" />}
        {can('income_tax', 'view') && <NavComponent to="/income-tax" icon={FileText} label="Income Tax" onClick={close} />}
        {can('tds', 'view') && <NavComponent to="/tds" icon={Receipt} label="TDS" onClick={close} />}
        {can('gst', 'view') && <NavComponent to="/gst" icon={Scale} label="GST" onClick={close} />}
        {can('audit', 'view') && <NavComponent to="/audit" icon={FileSearch} label="Audit" onClick={close} />}

        {/* Accounts sub-menu */}
        {can('accounts', 'view') && <div>
          <button
            onClick={() => setAccountsOpen(o => !o)}
            className={isCupertino ? 'cupertino-nav-item w-full flex items-center gap-2.5' : 'nav-item w-full flex items-center gap-3 py-2.5 text-sm'}
            style={isCupertino
              ? {
                  fontSize: '13px', fontWeight: 400, color: '#1D1D1F', opacity: 0.75,
                  padding: '7px 12px', borderRadius: '8px', width: '100%',
                }
              : {
                  borderLeft: '3px solid transparent',
                  color: 'var(--sidebar-text)',
                  paddingLeft: '17px',
                  paddingRight: '20px',
                }}
          >
            <BookOpen size={isCupertino ? 15 : 17} style={isCupertino ? { color: '#86868B' } : undefined} />
            <span className="nav-label flex-1 text-left whitespace-nowrap">Accounts</span>
            <ChevronDown size={13} className={`rail-hide transition-transform duration-200 ${accountsOpen ? 'rotate-180' : ''}`} />
          </button>
          {accountsOpen && (
            <div style={{ background: isCupertino ? 'transparent' : 'var(--sidebar-sub-menu-bg)' }}>
              {[
                { to: '/accounts/bookkeeping', label: 'Bookkeeping' },
                { to: '/accounts/bank-recon', label: 'Bank Recon' },
                { to: '/accounts/finalization', label: 'Finalization' },
                { to: '/accounts/mis', label: 'MIS Reports' },
              ].map(sub => (
                <NavLink
                  key={sub.to}
                  to={sub.to}
                  onClick={close}
                  className={isCupertino ? 'cupertino-nav-item flex items-center py-1.5 text-xs' : 'flex items-center pl-12 pr-5 py-2 text-xs transition-colors'}
                  style={({ isActive }) => isCupertino
                    ? {
                        fontSize: '12px', paddingLeft: '38px', paddingRight: '12px',
                        borderRadius: '8px',
                        color: '#1D1D1F', opacity: isActive ? 1 : 0.6,
                        fontWeight: isActive ? 600 : 400,
                        background: isActive ? '#EDEDF0' : 'transparent',
                      }
                    : {
                        color: isActive ? 'var(--sidebar-accent)' : 'var(--sidebar-text-muted)',
                        fontWeight: isActive ? 600 : 400,
                      }}
                >
                  {sub.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>}

        {can('roc', 'view') && <NavComponent to="/roc" icon={Building2} label="ROC" onClick={close} />}
        {can('esipf', 'view') && <NavComponent to="/esipf" icon={Landmark} label="ESI / PF" onClick={close} />}

        {isCupertino && <CSectionLabel label="Work" />}
        {can('tasks', 'view') && <NavComponent to="/tasks" icon={ClipboardList} label="Tasks" onClick={close} />}
        {can('meetings', 'view') && <NavComponent to="/meetings" icon={CalendarDays} label="Meetings" onClick={close} />}
        {can('checklists', 'view') && <NavComponent to="/checklists" icon={ListChecks} label="Checklists" onClick={close} />}
        {can('billing', 'view') && <NavComponent to="/billing" icon={CreditCard} label="Billing" onClick={close} />}
        {can('documents', 'view') && <NavComponent to="/documents" icon={FolderOpen} label="Documents" onClick={close} />}
        {can('dsc', 'view') && <NavComponent to="/dsc" icon={Shield} label="DSC Tracker" onClick={close} />}
        {can('calendar', 'view') && <NavComponent to="/calendar" icon={Calendar} label="Due Dates" onClick={close} />}
        {can('reports', 'view') && <NavComponent to="/reports" icon={BarChart3} label="Reports" onClick={close} />}

        {/* Divider */}
        {isCupertino
          ? <CSectionLabel label="Manage" />
          : <div className="my-2 mx-5" style={{ borderTop: '1px solid var(--sidebar-border)' }} />}

        <NavComponent to="/my-work" icon={Briefcase} label="My Work" onClick={close} />
        {/* Tools: client-side PDF utilities — no server data, so view is enough */}
        {can('tools', 'view') && <NavComponent to="/tools" icon={Wrench} label="Tools" onClick={close} />}
        {/* Personal Space: every role, no RBAC gate — private per-user area */}
        <NavComponent to="/personal" icon={Lock} label="Personal Space" onClick={close} />
        {can('employees', 'view') && <NavComponent to="/employees" icon={UserCog} label="Employees" onClick={close} />}
        {isSuperAdmin && <NavComponent to="/settings" icon={Settings} label="Settings & Roles" onClick={close} />}
        {isAdmin && <NavComponent to="/archived-clients" icon={Archive} label="Archived Clients" onClick={close} />}
      </nav>

      {/* User footer */}
      <div className={collapsed ? 'px-0 py-4 flex justify-center' : 'px-5 py-4'} style={{ borderTop: `1px solid ${isCupertino ? 'var(--hairline)' : 'var(--sidebar-border)'}` }}>
        <div className={`flex items-center gap-3 ${collapsed ? '' : 'mb-3'}`}>
          <div
            className={`rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center font-bold ${isCupertino ? 'w-8 h-8 text-xs' : 'w-9 h-9 shadow text-sm'}`}
            style={{ background: 'var(--sidebar-user-av-bg)', color: 'var(--sidebar-user-av-color)' }}
          >
            {user?.profile_photo
              ? <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
              : user?.name?.[0]}
          </div>
          <div className="min-w-0 rail-hide">
            <div className={`font-medium truncate ${isCupertino ? 'text-xs' : 'text-sm'}`} style={{ color: 'var(--sidebar-text)', fontWeight: 500 }}>{user?.name}</div>
            <div className="text-xs capitalize truncate" style={{ color: 'var(--sidebar-text-muted)' }}>
              {user?.designation || user?.role?.replace(/_/g, ' ')}
            </div>
          </div>
        </div>
        <div className="flex gap-3 rail-hide">
          <NavLink
            to="/profile"
            onClick={close}
            className="flex items-center gap-1.5 text-xs transition-colors"
            style={{ color: 'var(--sidebar-text-muted)' }}
          >
            <UserCircle size={14} /> Profile
          </NavLink>
          <button
            onClick={() => { close(); logout(); }}
            className="flex items-center gap-1.5 text-xs transition-colors ml-auto"
            style={{ color: 'var(--sidebar-text-muted)' }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
export default function Layout() {
  const { user, logout, can } = useAuth();
  const { themeId } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  // Auto-collapsing icon rail: icons-only by default, expands on hover.
  // Mobile keeps the existing off-canvas behavior (always expanded when open).
  const [railHover, setRailHover] = useState(false);

  const close = () => setSidebarOpen(false);
  const isCupertino = themeId === 'cupertino';
  const collapsed = !railHover && !sidebarOpen;

  // Sub-menu can't render meaningfully in the collapsed rail
  useEffect(() => { if (collapsed && accountsOpen) setAccountsOpen(false); }, [collapsed, accountsOpen]);

  const isAdmin = hasRole(user, 'partner', 'senior_staff');
  const isSuperAdmin = hasRole(user, 'partner');
  const isSeniorPlus = isAdmin;

  const NavComponent = isCupertino ? CNavItem : NavItem;

  return (
    <div className="flex h-screen" style={{ background: 'var(--main-bg)' }}>
      <Sidebar sidebarOpen={sidebarOpen} isCupertino={isCupertino} close={close} accountsOpen={accountsOpen} setAccountsOpen={setAccountsOpen} NavComponent={NavComponent} can={can} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} user={user} logout={logout}
        collapsed={collapsed} onRailEnter={() => setRailHover(true)} onRailLeave={() => setRailHover(false)} />
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={close} />}

      <div className={`flex-1 flex flex-col min-w-0 ${collapsed ? 'md:ml-16' : (isCupertino ? 'md:ml-[230px]' : 'md:ml-60')}`}
        style={{ transition: 'margin-left 0.18s ease' }}>
        {/* Topbar */}
        <header
          className={`flex items-center px-4 py-3 gap-3 flex-shrink-0 ${isCupertino ? '' : 'shadow-md'}`}
          style={{
            background: 'var(--topbar-bg)',
            borderBottom: '1px solid var(--topbar-border)',
            color: 'var(--topbar-text)',
          }}
        >
          <button className="md:hidden" style={{ color: 'var(--topbar-text)' }} onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="hidden md:block">
            <span className="font-bold text-lg tracking-wide" style={{ color: 'var(--sidebar-accent)' }}>
              Krishna Uday &amp; Associates
            </span>
            <span className="text-sm ml-2 opacity-60" style={{ color: 'var(--topbar-text)' }}>
              — Practice Management System
            </span>
          </div>
          <div className="md:hidden font-bold text-base" style={{ color: 'var(--sidebar-accent)' }}>KU &amp; Associates</div>

          <div className="ml-auto flex items-center gap-2">
            <ThemeSwitcherButton />
            <NotificationBell />
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium" style={{ color: 'var(--topbar-text)' }}>{user?.name}</div>
              <div className="text-xs capitalize opacity-60" style={{ color: 'var(--topbar-text)' }}>
                {user?.designation || user?.role?.replace(/_/g, ' ')}
              </div>
            </div>
            <UserDropdown user={user} logout={logout} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6 page-fade-in">
          <Outlet />
        </main>
        {/* App-wide wellness reminder engine (per-user, settings in Personal Space) */}
        <WellnessReminders />
      </div>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center"
        style={{ background: 'var(--sidebar-bg)', borderTop: '1px solid var(--sidebar-border)' }}
      >
        {[
          { to: '/', icon: LayoutDashboard, label: 'Home', exact: true },
          { to: '/clients', icon: Users, label: 'Clients' },
          { to: '/tasks', icon: ClipboardList, label: 'Tasks' },
          { to: '/notifications', icon: null, label: 'Alerts', isNotif: true },
          { to: '/my-work', icon: Briefcase, label: 'My Work' },
        ].map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            className="flex-1 flex flex-col items-center py-2 px-1 text-center transition-colors"
            style={({ isActive }) => ({ color: isActive ? 'var(--sidebar-accent)' : 'var(--sidebar-text-muted)' })}
          >
            {item.isNotif ? <NotificationBell compact /> : item.icon && <item.icon size={20} />}
            <span className="text-xs mt-0.5 leading-tight">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
